import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AdminService } from '../../../services/admin/admin.service';
import { ToasterService } from '../../../services/toaster/toaster.service';
import { GiveAwardModel } from '../../../models/admin/give-award-model';
import { ResetPasswordOverrideModel } from '../../../models/admin/reset-password-override-model';

@Component({
  selector: 'app-admin-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './admin-modal.html',
  styleUrl: './admin-modal.scss',
})
export class AdminModal implements OnInit {
  private adminService = inject(AdminService);
  private toaster = inject(ToasterService);

  public icons = this.adminService.icons;

  // Form states for password reset
  public usernameForPassword = signal('');
  public newPassword = signal('');
  public confirmNewPassword = signal('');

  // Form states for awarding icon
  public usernameForIcon = signal('');
  public selectedPortraitId = signal<number | null>(null);
  public expirationDate = signal('');

  ngOnInit() {
    this.adminService.getIcons();
  }

  resetPassword() {
    const user = this.usernameForPassword().trim();
    const pass = this.newPassword().trim();
    const confirmPass = this.confirmNewPassword().trim();

    if (!user || !pass || !confirmPass) {
      this.toaster.danger('Please fill in all the password reset fields.');
      return;
    }

    if (pass !== confirmPass) {
      this.toaster.danger('Passwords do not match.');
      return;
    }

    const model = new ResetPasswordOverrideModel();
    model.userName = user;
    model.newPassword = pass;
    model.confirmNewPassword = confirmPass;

    this.adminService.overridePassword(model).subscribe({
      next: () => {
        this.toaster.success(`Successfully reset password for user: ${user}`);
        this.usernameForPassword.set('');
        this.newPassword.set('');
        this.confirmNewPassword.set('');
      },
      error: (err: any) => {
        console.error(err);
        this.toaster.danger(err?.error?.message || `Failed to reset password for user: ${user}`);
      },
    });
  }

  awardIcon() {
    const user = this.usernameForIcon().trim();
    const portraitId = this.selectedPortraitId();
    const date = this.expirationDate();

    if (!user || portraitId === null || !date) {
      this.toaster.danger('Please select a user, an icon, and an expiration date.');
      return;
    }

    const model = new GiveAwardModel();
    model.userName = user;
    model.iconId = portraitId;
    model.expirationDate = date;

    this.adminService.giveAward(model).subscribe({
      next: () => {
        this.toaster.success(`Successfully awarded icon to user: ${user}`);
        this.usernameForIcon.set('');
        this.selectedPortraitId.set(null);
        this.expirationDate.set('');
      },
      error: (err: any) => {
        console.error(err);
        this.toaster.danger(err?.error?.message || `Failed to award icon to user: ${user}`);
      },
    });
  }

  getIconName(iconId: number | null): string {
    if (iconId === null) return 'Select Icon';
    const found = this.icons().find((p) => p.id === iconId);
    return found ? (found.description ? found.description : found.fileName) : 'Unknown Icon';
  }
}

